import {Dispatch, SetStateAction} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {EventRSVPStatus, useSetAttendance} from './useEvent';
import {useBookmarkQueue} from './useBookmarkQueue';

interface UseBookmarkActionsOptions {
  bookmarkedIds: Set<string>;
  initialBookmarkedIds: Set<string>;
  setSessionBookmarkedIds: Dispatch<SetStateAction<Set<string>>>;
}

// Returns a new array with the matching id removed, or the SAME array reference when the
// id wasn't present. Reference stability matters: the SwiperSlider used by the carousel
// remounts when its `items` prop changes identity, so handing every section a fresh
// array on every cache write would make unrelated sections (e.g. Hyper-Local when the
// user bookmarked from Coming Up) visibly "refresh". A cleaner long-term fix would be
// to stabilize the carousel's render via stable item keys, but that is upstream work.
function removeFromArrayIfPresent(arr: any[] | undefined, articleId: string): any[] | undefined {
  if (!arr || !arr.some((a: any) => a.id?.toString() === articleId)) return arr;
  return arr.filter((a: any) => a.id?.toString() !== articleId);
}

// Move an article from any discover section into bookmarked_upcoming and bump
// meta.bookmarked_upcoming_count. Items prepend to bookmarked_upcoming without
// re-sorting — the next feed refetch will resort to match the server's order. The
// count is only incremented when an article actually got inserted, so a re-bookmark
// after an unbookmark within the debounce window doesn't drift the count.
function moveArticleToSaved(
  queryClient: ReturnType<typeof useQueryClient>,
  articleId: string,
) {
  queryClient.setQueryData(['bookmark-ahead-feed'], (oldData: any) => {
    if (!oldData?.data?.data) return oldData;
    const sections = oldData.data.data;

    const findIn = (arr: any[]) =>
      (arr || []).find((a: any) => a.id?.toString() === articleId);

    const alreadySaved = !!findIn(sections.bookmarked_upcoming);
    const article =
      findIn(sections.hyper_local) ||
      findIn(sections.system_popular) ||
      findIn(sections.general_upcoming);

    let nextSaved = sections.bookmarked_upcoming;
    let inserted = false;
    if (article && !alreadySaved) {
      nextSaved = [{...article, is_bookmarked: true}, ...(nextSaved || [])];
      inserted = true;
    }

    const currentCount = oldData.data.meta?.bookmarked_upcoming_count ?? 0;
    return {
      ...oldData,
      data: {
        ...oldData.data,
        data: {
          ...sections,
          hyper_local: removeFromArrayIfPresent(sections.hyper_local, articleId),
          system_popular: removeFromArrayIfPresent(sections.system_popular, articleId),
          general_upcoming: removeFromArrayIfPresent(sections.general_upcoming, articleId),
          bookmarked_upcoming: nextSaved,
        },
        meta: {
          ...oldData.data.meta,
          bookmarked_upcoming_count: inserted ? currentCount + 1 : currentCount,
        },
      },
    };
  });
}

// Remove an article from bookmarked_upcoming and decrement the count. Discover
// sections are not touched: an unbookmarked card is allowed to vanish from the page
// until the next feed refetch puts it back if it qualifies.
function removeArticleFromSaved(
  queryClient: ReturnType<typeof useQueryClient>,
  articleId: string,
) {
  queryClient.setQueryData(['bookmark-ahead-feed'], (oldData: any) => {
    if (!oldData?.data?.data) return oldData;
    const sections = oldData.data.data;
    const saved = sections.bookmarked_upcoming || [];
    const wasPresent = saved.some((a: any) => a.id?.toString() === articleId);
    const filtered = wasPresent
      ? saved.filter((a: any) => a.id?.toString() !== articleId)
      : saved;
    const currentCount = oldData.data.meta?.bookmarked_upcoming_count ?? 0;
    return {
      ...oldData,
      data: {
        ...oldData.data,
        data: {
          ...sections,
          bookmarked_upcoming: filtered,
        },
        meta: {
          ...oldData.data.meta,
          bookmarked_upcoming_count: wasPresent
            ? Math.max(0, currentCount - 1)
            : currentCount,
        },
      },
    };
  });
}

export function useBookmarkActions({
  bookmarkedIds,
  initialBookmarkedIds,
  setSessionBookmarkedIds,
}: UseBookmarkActionsOptions) {
  const queryClient = useQueryClient();
  const {mutateAsync: setAttendance} = useSetAttendance();
  const {enqueue} = useBookmarkQueue();

  const handleBookmark = (article: any) => {
    const articleId = article.id.toString();
    const articleType = article.type;
    const isCurrentlyBookmarked = bookmarkedIds.has(articleId);
    const wasInitiallyBookmarked = initialBookmarkedIds.has(articleId);
    const willBeBookmarked = !isCurrentlyBookmarked;

    // Session-bookmark accounting drives the summary modal at exit. A re-toggle back
    // to the original state should leave the session set unchanged.
    if (willBeBookmarked && !wasInitiallyBookmarked) {
      setSessionBookmarkedIds((prev) => {
        const next = new Set(prev);
        next.add(articleId);
        return next;
      });
    } else if (!willBeBookmarked && !wasInitiallyBookmarked) {
      setSessionBookmarkedIds((prev) => {
        const next = new Set(prev);
        next.delete(articleId);
        return next;
      });
    }

    if (willBeBookmarked) {
      moveArticleToSaved(queryClient, articleId);
    } else {
      removeArticleFromSaved(queryClient, articleId);
    }

    const bookmarkType = articleType === 'meetup' ? 'meet-up' : articleType;

    // Auto-RSVP intent is forwarded to the queue so it only fires alongside a successful
    // bookmark POST. Without this, a tap+tap-undo within the debounce window collapses
    // to a no-op for the bookmark but the auto-RSVP would still go through, leaving the
    // event marked Interested while the user thinks they cancelled.
    const isEventLike =
      articleType === 'event' || articleType === 'meetup' || article.is_meetup;
    const currentRsvpStatus =
      article.rsvp_status?.status_value ?? EventRSVPStatus.NO_STATUS;
    const shouldAutoRsvp =
      isEventLike &&
      currentRsvpStatus !== EventRSVPStatus.INTERESTED &&
      currentRsvpStatus !== EventRSVPStatus.GOING;

    enqueue({
      articleId: parseInt(articleId, 10),
      type: bookmarkType,
      desired: willBeBookmarked,
      autoRsvp:
        willBeBookmarked && shouldAutoRsvp
          ? () =>
              setAttendance({
                eventId: parseInt(articleId, 10),
                prevStatus: currentRsvpStatus,
                status: EventRSVPStatus.INTERESTED,
                showDispatchRSVPModalPopupActions: false,
                showToast: false,
              }).catch(() => {
                // Silent: bookmark already succeeded, RSVP is secondary.
                console.warn('Failed to auto-RSVP as interested');
              })
          : undefined,
    });
  };

  return {handleBookmark};
}
